import type { OrganisationUpdateInput } from "../../../../clients/graphqlTypes.js";
import type {
  OnboardingFormValues,
  OnboardingLogo,
} from "./mergePrefillIntoFormValues.js";
import { toLogoInput } from "./onboardingLogo.js";
import { normalizeWebsiteUrl } from "./websiteUrl.js";

export type OnboardingFormVariant = "business" | "property";

const trimmedOrNull = (value: string): string | null => value.trim() || null;

// The step edits only the first phone; left without a number (optional) it is
// not sent, and the phones after it go out unchanged. `phones` replaces the
// organisation's phones, so when nothing is left the field is left out instead
// of erasing a stored phone with an empty list.
const phonesInput = (
  phones: OnboardingFormValues["phones"]
): OnboardingFormValues["phones"] => {
  const [firstPhone, ...otherPhones] = phones;
  return firstPhone && !firstPhone.number.trim() ? otherPhones : phones;
};

const logoChange = (
  logo: OnboardingLogo | null,
  initialLogo: OnboardingLogo | null
): Pick<OrganisationUpdateInput, "logo"> => {
  if (logo === null) {
    return initialLogo ? { logo: null } : {};
  }
  return logo.url === initialLogo?.url ? {} : { logo: toLogoInput(logo) };
};

const isBlank = (value: string | null | undefined): boolean =>
  !value || value.trim() === "";

// The address is optional: with street, postcode and city all blank (the preset
// country alone does not count) there is nothing to save, and a blank address
// must not replace a billing address the organisation already has.
const isAddressBlank = (address: OnboardingFormValues["address"]): boolean =>
  isBlank(address.streetLines1) &&
  isBlank(address.postalCode) &&
  isBlank(address.city);

/**
 * The `updateOrganisation` input of "Continue". Every part is optional, so only
 * what holds a value goes out and nothing erases data the organisation already
 * has: no empty email, no blank address, no empty legal name, managing director
 * or tax and legal details. A property owner saves only the contact details,
 * the address and, when filled, the property name as the organisation `title`
 * (empty: the title stays); a first phone without a number is left out. A
 * business also saves its name, website and logo. The website box shows the
 * stored website (`initialWebsite`), so clearing it there is a deliberate edit:
 * then `""` goes out and clears it. The logo goes out only when it differs from
 * `initialLogo`, the stored one: a new upload, or `null` for a removed logo.
 */
export function buildOrganisationUpdateInput({
  values,
  variant,
  initialLogo,
  initialWebsite,
}: {
  values: OnboardingFormValues;
  variant: OnboardingFormVariant;
  initialLogo: OnboardingLogo | null;
  initialWebsite: string | null;
}): OrganisationUpdateInput {
  const contactAndAddress: OrganisationUpdateInput = {
    ...(!isBlank(values.email) && { email: values.email }),
    ...(phonesInput(values.phones).length > 0 && {
      phones: phonesInput(values.phones),
    }),
    ...(!isAddressBlank(values.address) && {
      billingAddress: {
        streetLines1: values.address.streetLines1,
        postalCode: values.address.postalCode,
        city: values.address.city,
        countryCode: values.address.countryCode,
        // Without coordinates the server geocodes the address.
        location: values.address.location
          ? {
              lat: values.address.location.lat,
              lng: values.address.location.lng,
            }
          : undefined,
      },
    }),
  };
  if (variant === "property") {
    const title = trimmedOrNull(values.propertyName ?? "");
    return { ...contactAndAddress, ...(title && { title }) };
  }

  const website = values.website ? normalizeWebsiteUrl(values.website) : "";
  const managingDirector = trimmedOrNull(values.managingDirector);
  const vatNumber = trimmedOrNull(values.vatNumber);
  const taxNumber = trimmedOrNull(values.taxNumber);
  const registrationNumber = trimmedOrNull(values.registrationNumber);

  return {
    ...contactAndAddress,
    ...(!isBlank(values.fullLegalName) && {
      fullLegalName: values.fullLegalName,
    }),
    // "" clears the stored website; leaving the field out keeps it unchanged.
    ...((website || initialWebsite) && { website }),
    ...(managingDirector && { managingDirector }),
    ...(vatNumber && { vatNumber }),
    ...(taxNumber && { taxNumber }),
    ...(registrationNumber && { registrationNumber }),
    ...logoChange(values.logo, initialLogo),
  };
}
